import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getInitialMultiSelection,
  normalizeMultiSelection,
  resolveMultiSelection,
} from '@/components/select/multiSelection';
import {
  MultiSelectProps,
  SelectDefaultValue,
  SelectItem,
} from '@/components/select/selectBoxTypes';
import { arrayEqual } from '@/utils/array';

interface MultiSelectState<T> {
  selected: T[];
  isInitialized: boolean;
}

const useMultiSelectBox = <T extends SelectItem>(
  data: T[] | undefined,
  { value, onSelect }: MultiSelectProps<T>,
  defaultValue: SelectDefaultValue = 'none',
) => {
  const [state, setState] = useState<MultiSelectState<T>>({
    selected: getInitialMultiSelection(value),
    isInitialized: false,
  });
  const stateRef = useRef(state);
  const previousValueRef = useRef(value);
  stateRef.current = state;

  const applySelection = useCallback(
    (selected: T[], items?: T[]) => {
      setState({ selected, isInitialized: true });
      onSelect(selected, items);
    },
    [onSelect],
  );

  const handleChange = (selected: (T | null)[]) =>
    applySelection(normalizeMultiSelection(selected), data);
  const handleDeselect = () => applySelection([]);

  useEffect(() => {
    const currentState = stateRef.current;
    const hasExternalValueChanged = previousValueRef.current !== value;

    if (hasExternalValueChanged && value.length === 0) {
      previousValueRef.current = value;
      if (currentState.selected.length > 0) applySelection([]);
      return;
    }

    if (!data || data.length === 0) return;
    previousValueRef.current = value;

    if (hasExternalValueChanged || !currentState.isInitialized) {
      const resolvedSelection = resolveMultiSelection(data, value);
      if (
        resolvedSelection.length > 0 &&
        !arrayEqual<T>(resolvedSelection, currentState.selected)
      ) {
        applySelection(resolvedSelection, data);
        return;
      }
    }

    if (currentState.isInitialized) return;

    if (defaultValue === 'all') {
      applySelection(data, data);
      return;
    }

    setState((previousState) => ({ ...previousState, isInitialized: true }));
  }, [data, value, defaultValue, applySelection]);

  return { ...state, handleChange, handleDeselect };
};

export default useMultiSelectBox;
